import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import IRRModal from '../admin/FundOpportunityAdmin/IRRModal';
import IRRDataTable from '../../pages/admin/IRRManagement/IRRDataTable';
import IRRPeriodSelector from '../../pages/admin/IRRManagement/IRRPeriodSelector';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../styles/StyledButtons';
import { buildCashflowReportTemplateCsv, downloadCsv } from './cashflowReportCsvUtils';
import CashflowReportsManageModal from './CashflowReportsManageModal';
import type { IRRData } from '../../models/IRRDataPoint';
import type { IRRUpdate } from '../../models/IRRUpdate';
import type FundOpportunity from '../../models/FundOpportunity';

type ToastType = 'error' | 'warning' | 'info' | 'success';
interface Props {
  fundOpportunity: FundOpportunity;
  irrUpdates: IRRUpdate[];
  selectedLabel: string;
  irrData?: IRRData;
  loading: boolean;
  dataLoading: boolean;
  handleMonthChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  loadIRRUpdates: () => Promise<void>;
  onReportsChanged?: () => void;
  showToast: (message: string, type: ToastType) => void;
}

function ReportActions({
  count,
  onUpload,
  onManage,
}: {
  count: number;
  onUpload: () => void;
  onManage: () => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
      <StyledButton
        noTopSpacing
        buttonVariant="inactiveOrCancel"
        sx={SECONDARY_BUTTON_HOVER_SX}
        onClick={() =>
          downloadCsv(buildCashflowReportTemplateCsv(), 'cashflow-report-template.csv')
        }
      >
        Download Template
      </StyledButton>
      <StyledButton noTopSpacing onClick={onUpload}>
        Upload Cashflow Report
      </StyledButton>
      <StyledButton noTopSpacing buttonVariant="warning" onClick={onManage} disabled={count === 0}>
        Delete Report
      </StyledButton>
    </Box>
  );
}

function ReportData({
  irrUpdates,
  selectedLabel,
  irrData,
  loading,
  dataLoading,
  handleMonthChange,
}: Pick<
  Props,
  'irrUpdates' | 'selectedLabel' | 'irrData' | 'loading' | 'dataLoading' | 'handleMonthChange'
>) {
  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (irrUpdates.length === 0)
    return (
      <Typography variant="body1" sx={{ py: 2 }}>
        No cashflow reports available. Upload a cashflow report to get started.
      </Typography>
    );
  return (
    <>
      <IRRPeriodSelector
        irrUpdates={irrUpdates}
        selectedLabel={selectedLabel}
        dataLoading={dataLoading}
        onLabelChange={handleMonthChange}
        compact
      />
      <Box sx={{ mt: '-10px' }}>
        <IRRDataTable irrData={irrData} dataLoading={dataLoading} />
      </Box>
    </>
  );
}

export default function CashflowReportsExpanded(props: Props) {
  const [irrModalOpen, setIrrModalOpen] = useState(false);
  const [irrFile, setIrrFile] = useState<File>();
  const [irrMonth, setIrrMonth] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const handleUploadSuccess = (message: string, type: ToastType) => {
    props.showToast(message, type);
    if (type === 'success') {
      void props.loadIRRUpdates();
      props.onReportsChanged?.();
    }
  };
  return (
    <>
      <ReportActions
        count={props.irrUpdates.length}
        onUpload={() => setIrrModalOpen(true)}
        onManage={() => setManageModalOpen(true)}
      />
      <ReportData {...props} />
      <IRRModal
        open={irrModalOpen}
        fundOpportunityId={props.fundOpportunity.id}
        irrFile={irrFile}
        irrMonth={irrMonth}
        isSubmitting={isSubmitting}
        onClose={() => setIrrModalOpen(false)}
        onFileChange={setIrrFile}
        onMonthChange={setIrrMonth}
        onSubmittingChange={setIsSubmitting}
        onShowToast={handleUploadSuccess}
      />
      <CashflowReportsManageModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        fundOpportunity={props.fundOpportunity}
        irrUpdates={props.irrUpdates}
        onDeleted={() => {
          void props.loadIRRUpdates();
          props.onReportsChanged?.();
        }}
        onShowToast={props.showToast}
      />
    </>
  );
}
